import {
  AUTH_ERROR_MESSAGES,
  CHANTIERCARRE_CONTACT_URL,
  CHANTIERCARRE_WEBSITE_URL,
} from "@etudescarre/constants";
import { Button } from "@etudescarre/ui";
import { isNil, isNotNil } from "@etudescarre/utils";
import { EyeIcon, EyeSlashIcon } from "@phosphor-icons/react";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { useNavigate, useSearchParams } from "react-router";
import { LOGOUT_REASON_KEY } from "@/components/idle-timeout";
import { authClient } from "@/lib/auth-client";
import { resolveLoginDestination } from "@/lib/home-path";
import { ForgotPasswordModal } from "./forgot-password-modal";
import { SingleSessionModal } from "./single-session-modal";

type LoginFormData = {
  email: string;
  password: string;
};

/**
 * Only allow redirecting to an in-app, same-origin path. This rejects absolute
 * URLs (`https://evil.com`), protocol-relative URLs (`//evil.com`) and
 * backslash tricks (`/\evil.com`) that browsers may treat as external, which
 * would otherwise turn the `redirect` query param into an open redirect.
 */
function getSafeRedirect(path: string | null): string | null {
  if (isNil(path) || !path.startsWith("/")) return null;
  if (path.startsWith("//") || path.startsWith("/\\")) return null;
  return path;
}

function Login() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { refetch: refetchSession } = authClient.useSession();
  const [error, setError] = useState("");
  const [info, setInfo] = useState("");
  const [showPasswordReset, setShowPasswordReset] = useState(false);
  const [showContactLink, setShowContactLink] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [showSingleSession, setShowSingleSession] = useState(false);
  const [singleSessionLoading, setSingleSessionLoading] = useState(false);
  const [pendingNavigateTo, setPendingNavigateTo] = useState<string | null>(
    null,
  );

  const { register, handleSubmit, getValues, setFocus } =
    useForm<LoginFormData>({
      defaultValues: { email: "", password: "" },
    });

  useEffect(() => {
    setFocus("email");
  }, [setFocus]);

  // Explain an inactivity sign-out when redirected here by the idle timeout.
  useEffect(() => {
    if (sessionStorage.getItem(LOGOUT_REASON_KEY) === "inactivite") {
      setInfo("Vous avez été déconnecté en raison de votre inactivité.");
      sessionStorage.removeItem(LOGOUT_REASON_KEY);
    }
  }, []);

  const safeRedirect = getSafeRedirect(searchParams.get("redirect"));

  const clearError = () => {
    setError("");
    setShowPasswordReset(false);
    setShowContactLink(false);
  };

  const handleSubmitForm = async (data: LoginFormData) => {
    clearError();
    setIsLoading(true);

    try {
      const result = await authClient.signIn.email({
        email: data.email,
        password: data.password,
        fetchOptions: { throw: false },
      });

      if (result.error) {
        const { status, message } = result.error;

        if (message === AUTH_ERROR_MESSAGES.EMAIL_NOT_FOUND) {
          setError("Cette adresse n'est associée à aucun utilisateur.");
          setShowContactLink(true);
        } else if (message === AUTH_ERROR_MESSAGES.ACCOUNT_INACTIVE) {
          setError(
            "Votre compte est désactivé. Contactez votre administrateur.",
          );
        } else if (status === 403) {
          // A 403 without a known message is Better Auth's "email not verified"
          // case, reached only with the right password. An invited account
          // with no password yet gets a 401, like a wrong password: anything
          // else would reveal that state to whoever types the address.
          setError(
            "Veuillez créer votre mot de passe depuis le lien reçu par e-mail.",
          );
        } else if (status === 429) {
          setError(
            "Trop de tentatives de connexion. Veuillez réessayer dans quelques instants.",
          );
        } else {
          setError("Mot de passe incorrect.");
          setShowPasswordReset(true);
        }
        return;
      }

      // Fetch full session to read role from employee table
      const { data: session } = await authClient.getSession();
      await refetchSession();
      const role = session?.user.role;

      const destination = resolveLoginDestination(safeRedirect, role);

      // Single-session enforcement applies to field users only; staff
      // (root/admin) may legitimately stay signed in on several devices.
      if (role === "user") {
        const { data: sessions } = await authClient.listSessions();
        if (isNotNil(sessions) && sessions.length > 1) {
          setPendingNavigateTo(destination);
          setShowSingleSession(true);
          return;
        }
      }

      navigate(destination);
    } catch {
      setError("Une erreur est survenue. Veuillez réessayer.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleSingleSessionConfirm = async () => {
    setSingleSessionLoading(true);
    try {
      await authClient.revokeOtherSessions();
      navigate(pendingNavigateTo ?? "/");
    } catch {
      setError("Une erreur est survenue. Veuillez réessayer.");
      setShowSingleSession(false);
    } finally {
      setSingleSessionLoading(false);
    }
  };

  const handleSingleSessionCancel = async () => {
    setSingleSessionLoading(true);
    try {
      await authClient.signOut();
    } finally {
      setSingleSessionLoading(false);
      setShowSingleSession(false);
      setPendingNavigateTo(null);
    }
  };

  const emailRegister = register("email");
  const passwordRegister = register("password");
  const hasError = error !== "";

  return (
    <div className="flex min-h-screen" data-testid="login-page">
      <div
        className="hidden w-2/3 bg-cover bg-center lg:block"
        style={{ backgroundImage: "url(/login-bg.jpg)" }}
      >
        <div className="flex h-full items-end p-8">
          <p className="text-white text-sm">© ChantierCarré</p>
        </div>
      </div>

      <div className="flex w-full items-center justify-center bg-white px-8 lg:w-1/3">
        <div className="w-full max-w-md space-y-8">
          <div>
            <div className="mb-8">
              <img src="/logo.png" alt="ChantierCarré" className="h-12" />
            </div>
            <p className="text-gray-600">
              Merci de bien vouloir vous identifier
            </p>
          </div>

          {info && (
            <output className="block rounded-md bg-blue-50 p-3">
              <p className="text-blue-800 text-sm">{info}</p>
            </output>
          )}

          <form className="space-y-6" onSubmit={handleSubmit(handleSubmitForm)}>
            <div>
              <label
                className="mb-2 block font-medium text-gray-700 text-sm uppercase"
                htmlFor="email"
              >
                Adresse e-mail
              </label>
              <input
                {...emailRegister}
                onChange={(e) => {
                  emailRegister.onChange(e);
                  if (hasError) clearError();
                }}
                onInvalid={(e) => {
                  e.preventDefault();
                  setError("Cette adresse n'est pas valide.");
                }}
                aria-describedby={hasError ? "login-error" : undefined}
                aria-invalid={hasError || undefined}
                autoComplete="email"
                className="block w-full rounded-md border border-gray-300 px-3 py-2 text-gray-900 placeholder-gray-400 focus:border-[#1a3b5c] focus:outline-none focus:ring-1 focus:ring-[#1a3b5c]"
                id="email"
                placeholder="votre@email.com"
                required
                pattern="[^@\s]+@[^@\s]+\.[^@\s]+"
                type="email"
              />
            </div>

            <div>
              <label
                className="mb-2 block font-medium text-gray-700 text-sm uppercase"
                htmlFor="password"
              >
                Mot de passe
              </label>
              <div className="relative">
                <input
                  {...passwordRegister}
                  onChange={(e) => {
                    passwordRegister.onChange(e);
                    if (hasError) clearError();
                  }}
                  onInvalid={(e) => {
                    e.preventDefault();
                    setError("Veuillez saisir votre mot de passe.");
                  }}
                  aria-describedby={hasError ? "login-error" : undefined}
                  aria-invalid={hasError || undefined}
                  autoComplete="current-password"
                  className="block w-full rounded-md border border-gray-300 px-3 py-2 pr-10 text-gray-900 placeholder-gray-400 focus:border-[#1a3b5c] focus:outline-none focus:ring-1 focus:ring-[#1a3b5c]"
                  id="password"
                  minLength={1}
                  placeholder="Votre mot de passe"
                  required
                  type={showPassword ? "text" : "password"}
                />
                <button
                  aria-label={
                    showPassword
                      ? "Masquer le mot de passe"
                      : "Afficher le mot de passe"
                  }
                  className="absolute inset-y-0 right-0 flex items-center pr-3"
                  onClick={() => setShowPassword(!showPassword)}
                  type="button"
                >
                  {showPassword ? (
                    <EyeSlashIcon className="h-5 w-5 text-gray-400" />
                  ) : (
                    <EyeIcon className="h-5 w-5 text-gray-400" />
                  )}
                </button>
              </div>
            </div>

            {hasError && (
              <div
                className="rounded-md bg-red-50 p-3"
                data-testid="login-error"
                id="login-error"
                role="alert"
                aria-live="assertive"
              >
                <p className="text-red-800 text-sm">{error}</p>
                {showPasswordReset && (
                  <button
                    type="button"
                    onClick={() => setShowForgotPassword(true)}
                    className="mt-2 text-sm font-medium text-red-700 underline hover:text-red-900"
                  >
                    Renouveler mon mot de passe
                  </button>
                )}
                {showContactLink && (
                  <a
                    href={CHANTIERCARRE_CONTACT_URL}
                    className="mt-2 block text-sm font-medium text-red-700 underline hover:text-red-900"
                    target="chantiercarre"
                    rel="noopener noreferrer"
                  >
                    Formulaire de contact
                  </a>
                )}
              </div>
            )}

            <Button
              className="w-full bg-[#1a3b5c] hover:bg-[#2a4b6c]"
              disabled={isLoading}
              type="submit"
            >
              {isLoading ? "Connexion..." : "Se connecter"}
            </Button>

            <div className="text-center">
              <button
                type="button"
                onClick={() => setShowForgotPassword(true)}
                className="text-sm text-gray-500 underline hover:text-gray-700"
                data-testid="forgot-password-link"
              >
                Mot de passe oublié ?
              </button>
            </div>

            <div className="text-center">
              <p className="text-gray-500 text-sm">
                En savoir plus ?{" "}
                <a
                  className="text-gray-700 underline hover:text-gray-900"
                  href={CHANTIERCARRE_CONTACT_URL}
                  target="chantiercarre"
                  rel="noopener noreferrer"
                >
                  Contactez-nous
                </a>{" "}
                ou{" "}
                <a
                  className="text-gray-700 underline hover:text-gray-900"
                  href={CHANTIERCARRE_WEBSITE_URL}
                  target="chantiercarre"
                  rel="noopener noreferrer"
                >
                  visitez notre site web
                </a>
              </p>
            </div>
          </form>
        </div>
      </div>

      <ForgotPasswordModal
        open={showForgotPassword}
        onOpenChange={({ open }) => setShowForgotPassword(open)}
        defaultEmail={getValues("email")}
      />

      <SingleSessionModal
        open={showSingleSession}
        onConfirm={handleSingleSessionConfirm}
        onCancel={handleSingleSessionCancel}
        isLoading={singleSessionLoading}
      />
    </div>
  );
}

export default Login;
